import Script from 'next/script'

import { environment } from '@config/environment'
import {
  FLOORING_ORGANIZATION_LINKS,
  FURNITURE_ORGANIZATION_LINKS,
  SISALCARPET_ORGANIZATION_LINKS,
} from 'constants/organization'
import { SearchResultsHit } from '@components/searchbox/Results'
import { ContentfulPgPage, SiteDomain, Status } from 'types'
import { TmCatalogProps } from 'pages/catalog/[[...slug]]'
import { ObjFlooringWeave, WeaveStartingAtPriceResponse } from '@services/prices'
import { formatPrice } from '@utils/price'

const domain = environment.siteDomain

const organizationDataForGoogleMerchantCenter = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: domain === 'sisalcarpet' ? 'sisalcarpet.com' : domain === 'flooring' ? 'Curran' : 'curranonline.com',
      url:
        domain === 'sisalcarpet'
          ? environment.sisalcarpetRootUrl
          : domain === 'flooring'
          ? environment.flooringRootUrl
          : environment.furnitureRootUrl,
      parentOrganization: {
        name: 'Curran',
      },
      subOrganization:
        domain === 'flooring'
          ? {
              '@type': 'Organization',
              name: 'SynSisal',
              url: environment.synsisalRootUrl,
            }
          : undefined,
      sameAs:
        domain === 'sisalcarpet'
          ? SISALCARPET_ORGANIZATION_LINKS
          : domain === 'flooring'
          ? FLOORING_ORGANIZATION_LINKS
          : FURNITURE_ORGANIZATION_LINKS,
      description:
        domain === 'sisalcarpet'
          ? 'sisalcarpet.com is the premiere source for custom area rugs and wall-to-wall carpets crafted from timeless natural fibers like sisal, jute & wool.'
          : domain === 'flooring'
          ? 'Curran is a flooring distributor of natural flatwoven area rugs and carpet including sisal, jute, seagrass and other natural fibers, as well as the manufacturer of Synsisal, a recycled nylon sisal-look family of weaves that comes in area rugs, broadloom, and carpet tiles.'
          : 'Curran is the premier e-commerce resource for luxury European outdoor furniture and umbrellas',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-206-441-2721',
        url:
          domain === 'sisalcarpet'
            ? 'https://www.sisalcarpet.com/customer-service'
            : domain === 'flooring'
            ? 'https://www.curranfloor.com/customer-service'
            : 'https://www.curranonline.com/customer-service',
        contactType: 'Customer Service',
        email: 'service@curranfloor.com',
      },
      logo: {
        '@type': 'ImageObject',
        url:
          domain === 'flooring'
            ? 'https://res.cloudinary.com/curran-catalog/image/upload/v1764026272/Marketing/Logos%20and%20Branding/2026%20Curran%20Rebrand%20Logos/Curran_Logo_Moss_mtyyyz.svg'
            : 'https://res.cloudinary.com/curran-catalog/image/upload/v1672785150/Marketing/Logos%20and%20Branding/Co-branded%20Logos/CurranSisalcarpet_Logo_black_rgb_hywf32.png',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '1932 1st Ave, Suite 800',
        addressLocality: 'Seattle',
        addressRegion: 'WA',
        postalCode: '98101',
        addressCountry: 'US',
      },
    },
    {
      '@type': 'WebSite',
      url:
        domain === 'sisalcarpet'
          ? environment.sisalcarpetRootUrl
          : domain === 'flooring'
          ? environment.flooringRootUrl
          : environment.furnitureRootUrl,
      name: domain === 'sisalcarpet' ? 'sisalcarpet.com' : domain === 'flooring' ? 'Curran' : 'curranonline.com',
      potentialAction: {
        '@type': 'SearchAction',
        target:
          domain === 'sisalcarpet'
            ? environment.sisalcarpetRootUrl + 'catalog?query={search_term_string}'
            : domain === 'flooring'
            ? environment.flooringRootUrl + 'catalog?query={search_term_string}'
            : environment.furnitureRootUrl + 'catalog?query={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  ],
})

export const organizationDataForCatalog = (
  hits: SearchResultsHit[],
  props: ContentfulPgPage<TmCatalogProps>,
  serverFilterFacets: string | undefined,
  breadcrumb: string[],
) => {
  const baseUrl =
    environment.siteDomain === 'sisalcarpet'
      ? 'https://www.sisalcarpet.com/rugs/'
      : environment.siteDomain === 'flooring'
      ? environment.flooringRootUrl + 'rugs/'
      : 'https://www.curranonline.com/products/'
  // Plain link list (no nested Product entity) — Google's ItemList/Carousel validation would otherwise
  // require offers/review/aggregateRating on every item, which category pages can't reliably provide.
  // ref: https://developers.google.com/search/docs/appearance/structured-data/carousel
  const itemlist = hits
    ? hits.map((hit: SearchResultsHit, index) => {
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: hit.name,
          url: baseUrl + hit.nameSlug,
        }
      })
    : []

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Catalog',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: props.name,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: itemlist,
        },
      },
      {
        '@type': 'WebPage',
        '@id': props.slug,
        url:
          environment.siteDomain === 'sisalcarpet'
            ? environment.sisalcarpetRootUrl + props.slug
            : environment.siteDomain === 'flooring'
            ? environment.flooringRootUrl + props.slug
            : environment.furnitureRootUrl + props.slug,
        name: props.name,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumb,
      },
    ],
  })
}

const getWeaveRootUrl = () => {
  switch (environment.siteDomain) {
    case SiteDomain.SISALCARPET:
      return environment.sisalcarpetRootUrl
    case SiteDomain.SYNSISAL:
      return environment.synsisalRootUrl
    default:
      return environment.flooringRootUrl
  }
}

type FlooringProductWithPricing = ObjFlooringWeave & { startingAtPrice?: WeaveStartingAtPriceResponse | null }

const getErpWeaveAttribute = (product: ObjFlooringWeave, key: string): string | undefined => {
  const match = product.erpWeaveAttributes?.find((attribute) => Object.keys(attribute)[0] === key)
  return match ? Object.values(match)[0] : undefined
}

/**
 * Product/BreadcrumbList JSON-LD for an individual rug/weave product-detail page.
 *
 * Rug pricing isn't one flat number — a weave can be sold as a fixed-price box, a "starting at"
 * area rug, and/or per-square-yard broadloom, or require a quote entirely. Google's structured
 * data guidelines require every Product to carry a real, orderable price (never $0 or invented),
 * so quote-only weaves and weaves with no resolvable price omit the Product entity altogether
 * rather than ship an incomplete/misleading one.
 * ref: https://developers.google.com/search/docs/appearance/structured-data/product
 */
export const productDataForFlooringProduct = (product: FlooringProductWithPricing) => {
  const rootUrl = getWeaveRootUrl()
  const productUrl = `${rootUrl}rugs/${product.weaveNameSlug}`

  const graph: Record<string, unknown>[] = []

  if (!product.callForQuote) {
    const areaRugPrice = product.startingAtPrice?.area_rug_price ?? undefined
    const tileBoxPrice = product.startingAtPrice?.tile_box_price ?? undefined
    const sqydPrice = product.startingAtPrice?.bl_w2w_sqyd_price ?? undefined

    let offers: Record<string, unknown> | undefined
    const additionalProperty: Record<string, unknown>[] = []

    if (typeof areaRugPrice === 'number') {
      // "Starting at" price is only valid because it's the real, purchasable base configuration
      // price returned by the pricing API — not an invented minimum.
      offers = {
        '@type': 'Offer',
        url: productUrl,
        price: areaRugPrice,
        priceCurrency: 'USD',
        itemCondition: 'https://schema.org/NewCondition',
        availability: 'https://schema.org/InStock',
      }
      if (typeof sqydPrice === 'number') {
        // Keep the per-sqyd price descriptive rather than a second competing Offer/unit conversion.
        additionalProperty.push({
          '@type': 'PropertyValue',
          name: 'Wall-to-wall price',
          value: `${formatPrice(sqydPrice)} USD per square yard`,
        })
      }
    } else if (typeof tileBoxPrice === 'number') {
      offers = {
        '@type': 'Offer',
        url: productUrl,
        price: tileBoxPrice,
        priceCurrency: 'USD',
        itemCondition: 'https://schema.org/NewCondition',
        availability: 'https://schema.org/InStock',
      }
    } else if (typeof sqydPrice === 'number') {
      // Pure per-unit broadloom, no fixed/"starting at" price exists — the unit price IS the offer.
      offers = {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'USD',
        itemCondition: 'https://schema.org/NewCondition',
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: sqydPrice,
          priceCurrency: 'USD',
          unitText: 'per square yard',
        },
      }
    }

    if (offers) {
      const fiber = getErpWeaveAttribute(product, 'fiber')
      const countryOfOrigin = getErpWeaveAttribute(product, 'countryOfOrigin')
      const activeColorNames = product.colorGroup?.weaveColors
        ?.filter((color) => color.status === Status.ACTIVE)
        .map((color) => color.weaveColorName)
        .filter(Boolean)

      if (activeColorNames?.length) {
        additionalProperty.push({
          '@type': 'PropertyValue',
          name: 'Available colors',
          value: activeColorNames.join(', '),
        })
      }

      graph.push({
        '@type': 'Product',
        '@id': `${productUrl}#product`,
        url: productUrl,
        name: product.weaveName,
        description: product.metaDescription || product.weaveName,
        ...(product.images?.length ? { image: product.images.map((image) => image.secure_url).filter(Boolean) } : {}),
        // Never surface `vendor.vendorName` here — for flooring/synsisal that's the manufacturing
        // supplier, not a public retail brand (unlike furniture, where vendor IS the shown brand).
        brand: {
          '@type': 'Brand',
          name: product.keywords?.some((keyword) => keyword.toLowerCase() === 'synsisal') ? 'SynSisal' : 'Curran',
        },
        ...(product.materialFamily ? { category: product.materialFamily } : {}),
        ...(fiber ? { material: fiber } : {}),
        ...(countryOfOrigin ? { countryOfOrigin: { '@type': 'Country', name: countryOfOrigin } } : {}),
        offers,
        ...(additionalProperty.length ? { additionalProperty } : {}),
      })
    }
  }

  graph.push({
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: rootUrl },
      { '@type': 'ListItem', position: 2, name: 'All Weaves', item: `${rootUrl}catalog` },
      { '@type': 'ListItem', position: 3, name: product.weaveName, item: productUrl },
    ],
  })

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
}

export const Scripts = () =>
  environment.isProd ? (
    <>
      {/* ref: https://support.cookiebot.com/hc/en-us/articles/360009192739-Google-Tag-Manager-and-Automatic-cookie-blocking */}
      {/* Google Consent Mode */}
      <Script id="google-consent-mode" data-cookieconsent="ignore" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            dataLayer.push(arguments)
          }
          gtag("consent", "default", {
            ad_storage: "denied",
            analytics_storage: "denied",
            functionality_storage: "denied",
            personalization_storage: "denied",
            security_storage: "granted",
            wait_for_update: 500
          });
          gtag("set", "ads_data_redaction", true);
        `}
      </Script>

      {/* End Google Consent Mode */}
      {/* Google Tag Manager */}
      <Script id="google-tag-manager" data-cookieconsent="ignore" strategy="lazyOnload">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${environment.analytics.gtmId}');
        `}
      </Script>
      {/* End Google Tag Manager */}
      {/* Cookiebot CMP */}
      <Script
        id="Cookiebot"
        src="https://consent.cookiebot.com/uc.js"
        data-cbid={environment.analytics.cookiebotId}
        data-blockingmode="auto"
        type="text/javascript"
        strategy="lazyOnload"
      />
      {/* End Cookiebot CMP */}

      {/* Cookiebot -> Consent Mode bridge */}
      <Script id="cookiebot-consent-bridge" strategy="afterInteractive">
        {`
          // Map Cookiebot categories -> Consent Mode v2 signals
          function updateConsentFromCookiebot() {
            try {
              if (!window.Cookiebot) return;
              var c = window.Cookiebot;
              var marketing = c.consent && c.consent.marketing;
              var statistics = c.consent && c.consent.statistics;
              var preferences = c.consent && c.consent.preferences;

              gtag('consent', 'update', {
                ad_storage: marketing ? 'granted' : 'denied',
                ad_user_data: marketing ? 'granted' : 'denied',
                ad_personalization: marketing ? 'granted' : 'denied',
                analytics_storage: statistics ? 'granted' : 'denied',
                functionality_storage: preferences ? 'granted' : 'denied',
                personalization_storage: (preferences || marketing) ? 'granted' : 'denied',
                // security_storage remains granted by default
              });
            } catch (e) { /* no-op */ }
          }

          // Initial run once Cookiebot initializes
          window.addEventListener('CookiebotOnLoad', updateConsentFromCookiebot);
          // When user changes consent
          window.addEventListener('CookiebotOnAccept', updateConsentFromCookiebot);
          window.addEventListener('CookiebotOnDecline', updateConsentFromCookiebot);
        `}
      </Script>
      {/* Global Site Code Pixel - Facebook Pixel */}
      <Script
        id="fb-pixel"
        strategy="lazyOnload"
        data-cookieconsent="ignore"
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', ${environment.analytics.fPixelId});
              fbq('track', 'PageView');
          `,
        }}
      />
      {/* End Global Site Code Pixel - Facebook Pixel */}
      {/* Start of HubSpot Embed Code */}
      <Script
        type="text/javascript"
        id="hs-script-loader"
        async
        defer
        src="//js.hs-scripts.com/21019888.js"
        strategy="lazyOnload"
      />
      {/* End of HubSpot Embed Code */}
      {/* Structured data Organization  */}
      {(domain === 'sisalcarpet' || domain === 'flooring' || domain === 'furniture') && (
        <script
          id="structured-data-organization"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: organizationDataForGoogleMerchantCenter }}
        />
      )}
      {/* End of Structured data Organization  */}
    </>
  ) : null
